"use client";

import { FooterLinkSection } from "@/components/utility/FooterLinkSection";
import { Mail, Phone } from "@mui/icons-material";
import InstagramIcon from "@mui/icons-material/Instagram";
import {
  Box,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import Image from "next/image";
import { ReactElement } from "react";

// Data for footer columns
const companyLinks = [
  { text: "Tentang Kami", href: "/about-us" },
  { text: "Hubungi Kami", href: "/contact-us" },
];

const helpLinks = [
  { text: "Syarat dan Ketentuan", href: "/terms-and-condition" },
  { text: "Kebijakan Privasi", href: "/privacy-policy" },
];

export const FooterSection = (): ReactElement => {
  return (
    <Box
      component="footer"
      sx={{
        bgcolor: "primary.main",
        width: "100%",
      }}
      className="w-full"
    >
      <Container maxWidth="lg">
        <Stack>
          <Grid
            container
            spacing={{ xs: 6, md: 6 }}
            className="justify-center md:justify-between mx-6 mt-16"
          >
            <Grid
              size={{
                xs: 12,
                md: 3,
              }}
            >
              <Image
                src="/assets/images/logo-square-white-bg.png"
                alt="logo-square-white-bg"
                width={98}
                height={0}
                style={{ height: "auto" }}
                className="mb-6"
              />
              <Box>
                <Typography
                  variant="body2"
                  color="white"
                  className="font-bold text-sm flex-none order-1 grow-0 leading-relaxed hyphens-auto"
                  lang="id"
                >
                  Gedung Artha Graha Lantai 9, Sudirman Central Business
                  District, Lot 25.
                </Typography>
                <Box className="mb-2" />
                <Typography
                  variant="body2"
                  color="white"
                  className="font-normal text-xs flex-none order-1 grow-0 leading-relaxed hyphens-auto"
                  lang="id"
                >
                  Jalan Jend. Sudirman Kavling 52 - 53 Jakarta Selatan, DKI
                  Jakarta
                </Typography>
              </Box>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: "auto",
              }}
            >
              <FooterLinkSection title="Perusahaan" links={companyLinks} />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: "auto",
              }}
            >
              <FooterLinkSection title="Bantuan" links={helpLinks} />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4.5,
              }}
            >
              <Stack spacing={2}>
                <Typography
                  color="white"
                  variant="body2"
                  className="text-md font-bold"
                >
                  Ikuti Kami
                </Typography>
                <Stack direction="column" spacing={4} alignItems="start">
                  <Stack direction="row" spacing={3} alignItems="center">
                    <IconButton
                      component="a"
                      href="https://www.instagram.com/umkmsofficial/?e=947a3ee5-b86b-4b98-a665-084950435363&g=5"
                      size="medium"
                      className="text-white p-0"
                      disableRipple
                      disableFocusRipple
                      disableTouchRipple
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <InstagramIcon fontSize="inherit" />
                    </IconButton>
                  </Stack>

                  <Box
                    component="a"
                    href="https://play.google.com/store/apps/details?id=com.umkmpintar.umkms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Image
                      src={"/assets/images/app-store/badge-play-store.svg"}
                      alt={"badge-play-store"}
                      width={130}
                      height={0}
                      style={{ height: "auto" }}
                    />
                  </Box>

                  <Box className="flex flex-col gap-3">
                    <Typography
                      variant="body2"
                      color="initial"
                      className="text-xs text-white leading-relaxed text-wrap hyphens-auto"
                    >
                      Layanan Pengaduan Konsumen : PT UMKM PINTAR INDONESIA
                    </Typography>
                    <Typography
                      variant="body2"
                      color="initial"
                      className="text-xs text-white leading-relaxed inline-flex items-center"
                    >
                      <Phone fontSize="small" className="mr-1.5" />
                      <Link
                        href="tel:+6282123496617"
                        color="inherit"
                        underline="hover"
                      >
                        0821-2349-6617
                      </Link>
                    </Typography>
                    <Typography
                      variant="body2"
                      color="initial"
                      className="text-xs text-white leading-relaxed inline-flex items-center"
                    >
                      <Mail fontSize="small" className="mr-1.5" />
                      <Link
                        href="mailto:support@umkms.com"
                        color="inherit"
                        underline="hover"
                      >
                        support@umkms.com
                      </Link>
                    </Typography>
                  </Box>

                  <Box className="flex flex-col gap-3">
                    <Typography
                      variant="body1"
                      color="initial"
                      className="text-xs text-white leading-relaxed text-wrap hyphens-auto"
                      lang="id"
                    >
                      Direktorat Jenderal Perlindungan Konsumen dan Tertib
                      Kementerian Perdagangan Republik Indonesia
                    </Typography>
                    <Typography
                      variant="body1"
                      color="initial"
                      className="text-xs text-white leading-relaxed inline-flex items-center"
                    >
                      <Phone fontSize="small" className="mr-1.5" />
                      <Link
                        href="tel:+6285311111010"
                        color="inherit"
                        underline="hover"
                      >
                        0853-1111-1010
                      </Link>
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Grid>
          </Grid>

          <Divider className="bg-white mt-16 mb-10" />

          <Box className="text-center mb-10" sx={{ textAlign: "center" }}>
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: 14,
                color: "white",
                textAlign: "center",
              }}
            >
              Copyright © {new Date().getFullYear()} PT Umkm Pintar Indonesia
            </Typography>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};
